import { useEffect, type ButtonHTMLAttributes, type ReactNode } from "react";

export function Button({ variant = "default", ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "default" | "primary" | "quiet" | "danger" }) {
  return <button {...props} className={["btn", variant, props.className].filter(Boolean).join(" ")} />;
}

export function Card({ title, children, aside, className }: { title?: ReactNode; children: ReactNode; aside?: ReactNode; className?: string }) {
  return (
    <section className={["card", className].filter(Boolean).join(" ")}>
      {(title || aside) && <header className="card-head"><h3>{title}</h3>{aside}</header>}
      {children}
    </section>
  );
}

export function Badge({ children, tone = "neutral" }: { children: ReactNode; tone?: "neutral" | "info" | "warn" | "good" | "bad" }) {
  return <span className={`badge ${tone}`}>{children}</span>;
}

export function Table<T>({ rows, columns, onRowClick, empty }: { rows: T[]; columns: Array<{ key: string; header: ReactNode; render: (row: T) => ReactNode }>; onRowClick?: (row: T) => void; empty?: ReactNode }) {
  if (!rows.length) return <p className="muted">{empty ?? "Nothing here."}</p>;
  return (
    <table className="table">
      <thead><tr>{columns.map((c) => <th key={c.key}>{c.header}</th>)}</tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} onClick={onRowClick ? () => onRowClick(r) : undefined} className={onRowClick ? "clickable" : undefined}>
            {columns.map((c) => <td key={c.key}>{c.render(r)}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Drawer({ open, title, onClose, children }: { open: boolean; title: ReactNode; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <aside className="drawer" role="dialog" aria-label={typeof title === "string" ? title : "drawer"}>
      <header className="card-head"><h3>{title}</h3><button className="btn quiet" onClick={onClose} aria-label="Close">×</button></header>
      <div className="drawer-body">{children}</div>
    </aside>
  );
}

export function Field({ label, children, hint }: { label: ReactNode; children: ReactNode; hint?: ReactNode }) {
  return <label className="field"><span className="field-label">{label}</span>{children}{hint && <span className="muted small">{hint}</span>}</label>;
}

export function Empty({ children }: { children: ReactNode }) { return <p className="muted center">{children}</p>; }
